import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, forkJoin, of, timer } from 'rxjs';
import { catchError, map, mergeMap, retry, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { ChatMessage } from '../../../core/models/chat-message.model';
import { ConversationTurn } from '../../../core/models/conversation.model';
import { SlidePreview } from '../../../core/models/slide-preview.model';
import { Phase } from '../../../core/models/session.model';

import { AuthService } from '../../../core/services/auth.service';
import { SessionService } from '../../../core/services/session.service';
import { WebSocketService } from '../../../core/services/websocket.service';
import { SessionActions } from './session.actions';
import { selectSessionId, selectWebsocketUrl, selectMessages } from './session.selectors';

@Injectable()
export class SessionEffects {
  private readonly actions$ = inject(Actions);
  private readonly sessionService = inject(SessionService);
  private readonly wsService = inject(WebSocketService);
  private readonly authService = inject(AuthService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  readonly createSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.createSession),
      switchMap(({ s3HandoffKey }) =>
        this.sessionService.createSession(s3HandoffKey).pipe(
          map((session) => SessionActions.createSessionSuccess({ session })),
          catchError((error) =>
            of(SessionActions.createSessionFailure({ error: error.message }))
          )
        )
      )
    )
  );

  readonly loadHandoffOnCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.createSessionSuccess),
      switchMap(({ session }) =>
        this.sessionService.getHandoff(session.sessionId).pipe(
          map((handoff) => SessionActions.loadHandoffSuccess({ handoff })),
          catchError(() => EMPTY) // Silently ignore — card just won't render
        )
      )
    )
  );

  readonly submitInput$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.submitInput),
      mergeMap(({ sessionId, payload }) =>
        this.sessionService.submitInput(sessionId, payload).pipe(
          map(() => SessionActions.submitInputSuccess({ sessionId })),
          catchError((error) =>
            of(SessionActions.submitInputFailure({ error: error.message }))
          )
        )
      )
    )
  );

  readonly uploadLogo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.uploadLogo),
      mergeMap(({ sessionId, file }) =>
        this.sessionService.uploadLogo(sessionId, file).pipe(
          map((asset) => SessionActions.uploadLogoSuccess({ asset })),
          catchError((error) =>
            of(SessionActions.uploadLogoFailure({ error: error.message }))
          )
        )
      )
    )
  );

  readonly connectWebSocket$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SessionActions.submitInputSuccess),
        withLatestFrom(this.store.select(selectWebsocketUrl)),
        tap(([_, wsUrl]) => {
          if (wsUrl) {
            const token = this.authService.getToken();
            this.wsService.connect(wsUrl, token || '');
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * After a new session's WS connects, automatically send a "proceed" message
   * so the agent starts generating the deck without asking redundant questions.
   * The agent already has the user's preferences from POST /sessions/:id/input.
   */
  readonly autoStartGeneration$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SessionActions.websocketConnected),
        withLatestFrom(this.store.select(selectSessionId), this.store.select(selectMessages)),
        tap(([_, sessionId, messages]) => {
          // Only auto-send if there are no messages yet (new session, not a resume)
          if (sessionId && messages.length === 0) {
            const message = {
              type: 'user_message' as const,
              session_id: sessionId,
              content: 'Generate the presentation based on my preferences.',
              timestamp: new Date().toISOString(),
            };
            this.store.dispatch(SessionActions.messageSent({ message }));
          }
        })
      ),
    { dispatch: false }
  );

  readonly connectWebSocketOnHydrate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SessionActions.hydrateSessionSuccess),
        tap(({ session }) => {
          const wsUrl = session.websocketUrl;
          if (wsUrl) {
            const token = this.authService.getToken();
            this.wsService.connect(wsUrl, token || '');
          }
        })
      ),
    { dispatch: false }
  );

  readonly navigateToSession$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SessionActions.submitInputSuccess),
        withLatestFrom(this.store.select(selectSessionId)),
        tap(([_, sessionId]) => {
          if (sessionId) {
            this.router.navigate(['/session', sessionId]);
          }
        })
      ),
    { dispatch: false }
  );

  readonly messageSend$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SessionActions.messageSent),
        tap(({ message }) => {
          this.wsService.send(message);
        })
      ),
    { dispatch: false }
  );

  readonly websocketMessages$ = createEffect(() =>
    this.wsService.messages$.pipe(
      map((msg) => {
        switch (msg.type) {
          case 'agent_message':
            return SessionActions.messageReceived({
              message: {
                id: crypto.randomUUID(),
                type: 'agent',
                content: msg.content,
                phase: msg.phase,
                turnNumber: msg.turn_number,
                timestamp: msg.timestamp,
              },
            });
          case 'deck_preview':
            return SessionActions.previewReceived({
              slides: msg.slides.map((s) => {
                const rawUrl = (s as unknown as { preview_url?: string }).preview_url ?? s.previewUrl ?? '';
                // Only use the URL if it's a full URL (pre-signed). Relative paths are unusable.
                const previewUrl = rawUrl.startsWith('http') ? rawUrl : '';
                return {
                  index: s.index,
                  title: s.title,
                  previewUrl,
                };
              }),
              totalSlides: msg.total_slides,
              deckVersion: msg.deck_version,
            });
          case 'phase_change':
            return SessionActions.phaseChanged({
              phase: msg.phase ?? (msg as unknown as { to?: Phase }).to ?? null as unknown as Phase,
            });
          case 'export_ready':
            return SessionActions.exportReady({
              downloadUrl: msg.download_url,
              fileName: msg.file_name,
              expiresAt: msg.expires_at,
            });
          case 'error':
            return SessionActions.errorReceived({
              message: msg.message,
              retryAvailable: msg.retry_available,
            });
        }
      })
    )
  );

  readonly websocketConnectionState$ = createEffect(() =>
    this.wsService.connectionState$.pipe(
      map((state) => {
        switch (state) {
          case 'connected':
            return SessionActions.websocketConnected();
          case 'disconnected':
            return SessionActions.websocketDisconnected();
          case 'reconnecting':
            return SessionActions.websocketReconnecting();
        }
      })
    )
  );

  /** When a deck_preview arrives with unusable relative URLs, re-fetch pre-signed URLs from the API */
  readonly refreshPreviewsOnDeckEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SessionActions.previewReceived),
        withLatestFrom(this.store.select(selectSessionId)),
        switchMap(([{ slides }, sessionId]) => {
          const hasEmptyUrls = slides.some((s) => !s.previewUrl);
          if (!hasEmptyUrls || !sessionId) {
            return of(undefined);
          }
          // Wait for the downstream to generate preview images in S3, then fetch
          return timer(3000).pipe(
            switchMap(() => this.sessionService.getPreviews(sessionId).pipe(
              tap((response) => {
                if (response.previews.length > 0) {
                  this.store.dispatch(SessionActions.previewReceived({
                    slides: response.previews.map((p) => ({
                      index: p.index,
                      title: slides.find((s) => s.index === p.index)?.title ?? `Slide ${p.index + 1}`,
                      previewUrl: p.url,
                    })),
                    totalSlides: response.previews.length,
                    deckVersion: 1,
                  }));
                } else {
                  // No previews yet — throw to trigger retry
                  throw new Error('Previews not ready');
                }
              }),
            )),
            retry({ count: 3, delay: 3000 }),
            catchError(() => of(undefined)),
          );
        }),
      ),
    { dispatch: false }
  );

  readonly hydrateSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.hydrateSession),
      switchMap(({ sessionId }) =>
        forkJoin({
          session: this.sessionService.getSession(sessionId),
          conversation: this.sessionService.getConversation(sessionId).pipe(
            catchError(() => of({ sessionId, turns: [], total: 0 }))
          ),
          previews: this.sessionService.getPreviews(sessionId).pipe(
            catchError(() => of({ previews: [] }))
          ),
          handoff: this.sessionService.getHandoff(sessionId).pipe(
            catchError(() => of(null))
          ),
        }).pipe(
          map(({ session, conversation, previews, handoff }) =>
            SessionActions.hydrateSessionSuccess({
              session,
              messages: conversation.turns.map((turn) => this.turnToMessage(turn)),
              slides: previews.previews.map((p) => this.previewToSlide(p)),
              handoff,
            })
          ),
          catchError((error) =>
            of(SessionActions.hydrateSessionFailure({ error: error.message }))
          )
        )
      )
    )
  );

  /**
   * Persist userInput to sessionStorage so it survives page refreshes.
   */
  readonly persistUserInput$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SessionActions.submitInput),
        withLatestFrom(this.store.select(selectSessionId)),
        tap(([{ payload }, sessionId]) => {
          if (sessionId) {
            try {
              sessionStorage.setItem(`ppt_userInput_${sessionId}`, JSON.stringify(payload));
            } catch { /* quota exceeded — ignore */ }
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * Persist logoUrl to sessionStorage so it survives page refreshes.
   */
  readonly persistLogoUrl$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SessionActions.uploadLogoSuccess),
        withLatestFrom(this.store.select(selectSessionId)),
        tap(([{ asset }, sessionId]) => {
          if (sessionId) {
            try {
              sessionStorage.setItem(`ppt_logoUrl_${sessionId}`, asset.url);
            } catch { /* quota exceeded — ignore */ }
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * Restore userInput and logoUrl from sessionStorage after hydration.
   */
  readonly restoreLocalState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.hydrateSessionSuccess),
      map(({ session }) => {
        const userInputJson = sessionStorage.getItem(`ppt_userInput_${session.sessionId}`);
        const logoUrl = sessionStorage.getItem(`ppt_logoUrl_${session.sessionId}`);
        return SessionActions.restoreLocalState({
          userInput: userInputJson ? JSON.parse(userInputJson) : null,
          logoUrl: logoUrl ?? null,
        });
      })
    )
  );

  private turnToMessage(turn: ConversationTurn): ChatMessage {
    const phase = (turn.phase as Phase | null) ?? 'OPENING';
    const meta = (turn.metadata ?? {}) as Record<string, unknown>;
    const timestamp = (meta['timestamp'] as string | undefined) ?? turn.createdAt;

    return {
      id: turn.id,
      type: turn.role,
      content: turn.content,
      phase,
      turnNumber: turn.turnNumber,
      timestamp,
    };
  }

  private previewToSlide(preview: { index: number; url: string }): SlidePreview {
    return {
      index: preview.index,
      title: `Slide ${preview.index + 1}`,
      previewUrl: preview.url,
    };
  }
}
